import { useState, useRef, useEffect, useCallback } from "react";
import { useCompany } from "../../contexts/CompanyContext";
import type { HubTask, HubTaskStatus } from "../../mocks/hubTasks";

const EDGE_URL = `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/gemini-npc`;

interface MentorMessage {
  id: number;
  role: "user" | "mentor";
  text: string;
}

interface Props {
  tasks: HubTask[];
  taskStates?: Record<string, HubTaskStatus>;
  hubTitle?: string;
}

function buildAnalysisPrompt(tasks: HubTask[], taskStates: Record<string, HubTaskStatus>, hubTitle: string, capital: number, reputation: number): string {
  const resolved = tasks.map((t) => ({ ...t, status: taskStates[t.id] ?? t.status }));
  const total = resolved.length;
  const done = resolved.filter((t) => t.status === "done").length;
  const overdue = resolved.filter((t) => t.status === "overdue" || (t.status !== "done" && t.dDay < 0)).length;
  const urgent = resolved.filter((t) => t.status !== "done" && t.dDay >= 0 && t.dDay <= 3).length;
  const inprogress = resolved.filter((t) => t.status === "inprogress").length;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  const overdueList = resolved
    .filter((t) => t.status !== "done" && t.dDay < 0)
    .map((t) => `- ${t.title} (D+${Math.abs(t.dDay)})`)
    .join("\n");

  const urgentList = resolved
    .filter((t) => t.status !== "done" && t.dDay >= 0 && t.dDay <= 3)
    .map((t) => `- ${t.title} (D-${t.dDay})`)
    .join("\n");

  return `당신은 엔터테인먼트 업계 경력 20년의 경영 전략 컨설턴트 이지현입니다. 친근하고 따뜻하지만 핵심을 찌르는 스타일로 한국어로 답변하세요.

현재 ${hubTitle} 허브 진행 현황을 분석하고 맞춤 조언을 해주세요:

[허브 현황]
- 전체 태스크: ${total}건 중 ${done}건 완료 (${pct}%)
- 진행 중: ${inprogress}건
- 기한 초과: ${overdue}건
- 마감 임박(3일 이내): ${urgent}건
- 현재 자본금: ${capital.toLocaleString()}만원
- 명성 점수: ${reputation}pt

${overdueList ? `[기한 초과 태스크]\n${overdueList}\n` : ""}
${urgentList ? `[마감 임박 태스크]\n${urgentList}\n` : ""}

위 현황을 바탕으로:
1. 전반적인 진행 상태 평가 (짧게)
2. 가장 급하게 처리해야 할 사항 2-3가지 (구체적으로)
3. 리스크 요인과 대응 조언

총 200자 내외로 핵심만 짚어서 말해주세요.`;
}

export default function HubMentorPanel({ tasks, taskStates = {}, hubTitle = "허브" }: Props) {
  const { companyState } = useCompany();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<MentorMessage[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [analyzed, setAnalyzed] = useState(false);
  const idRef = useRef(0);
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const capital = companyState?.capital ?? 0;
  const reputation = companyState?.reputation ?? 0;

  const resolved = tasks.map((t) => ({ ...t, status: taskStates[t.id] ?? t.status }));
  const done = resolved.filter((t) => t.status === "done").length;
  const overdue = resolved.filter((t) => t.status !== "done" && t.dDay < 0).length;
  const urgent = resolved.filter((t) => t.status !== "done" && t.dDay >= 0 && t.dDay <= 3).length;
  const pct = tasks.length > 0 ? Math.round((done / tasks.length) * 100) : 0;

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const callGemini = useCallback(async (userMessage: string, context: string) => {
    setLoading(true);
    try {
      const history = messages.map((m) => ({ role: m.role === "user" ? "user" : "npc", text: m.text }));
      const res = await fetch(EDGE_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ npcType: "mentor", userMessage, history, context }),
      });
      const data = await res.json();
      idRef.current += 1;
      setMessages((prev) => [...prev, { id: idRef.current, role: "mentor", text: data.reply ?? "잠시 후 다시 시도해주세요." }]);
    } catch {
      idRef.current += 1;
      setMessages((prev) => [...prev, { id: idRef.current, role: "mentor", text: "연결에 문제가 생겼어요. 잠시 후 다시 시도해주세요." }]);
    } finally {
      setLoading(false);
    }
  }, [messages]);

  // 패널 열릴 때 자동 분석
  useEffect(() => {
    if (isOpen && !analyzed && tasks.length > 0) {
      setAnalyzed(true);
      const prompt = buildAnalysisPrompt(tasks, taskStates, hubTitle, capital, reputation);
      callGemini("현재 허브 진행 상황을 분석해주세요.", prompt);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    idRef.current += 1;
    setMessages((prev) => [...prev, { id: idRef.current, role: "user", text }]);
    const context = `${hubTitle} 허브 AI 멘토입니다. 현재 진행률 ${pct}%, 기한초과 ${overdue}건, 마감임박 ${urgent}건. 자본금 ${capital.toLocaleString()}만원, 명성 ${reputation}pt.`;
    await callGemini(text, context);
  };

  const handleKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const handleRefresh = () => {
    setMessages([]);
    setAnalyzed(false);
    setIsOpen(false);
    setTimeout(() => setIsOpen(true), 100);
  };

  return (
    <>
      {/* Floating toggle button */}
      <button
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        className={`fixed right-0 top-1/2 -translate-y-1/2 z-[90] flex flex-col items-center justify-center gap-1.5 px-2 py-4 rounded-l-xl border-l border-t border-b transition-all cursor-pointer whitespace-nowrap group ${
          isOpen
            ? "bg-[#D97706]/10 border-[#D97706]/30 text-[#D97706]"
            : "bg-[#111112] border-white/[0.08] text-[#555555] hover:text-[#D97706] hover:border-[#D97706]/30 hover:bg-[#D97706]/5"
        }`}
      >
        <i className={`ri-robot-2-line text-base transition-transform ${isOpen ? "rotate-12" : ""}`}></i>
        <div className="flex flex-col items-center gap-0.5">
          {"AI멘토".split("").map((c, i) => (
            <span key={i} className="text-[9px] font-semibold leading-tight">{c}</span>
          ))}
        </div>
        {(overdue > 0 || urgent > 0) && (
          <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse"></span>
        )}
      </button>

      {/* Side Panel Drawer */}
      <div
        className={`fixed right-0 top-0 h-screen z-[80] flex flex-col bg-[#0F0F11] border-l border-white/[0.07] shadow-2xl transition-all duration-300 ${
          isOpen ? "w-80 opacity-100" : "w-0 opacity-0 pointer-events-none"
        }`}
        style={{ top: "48px", height: "calc(100vh - 48px)" }}
      >
        {isOpen && (
          <>
            {/* Panel Header */}
            <div className="px-4 py-3.5 border-b border-white/[0.07] flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#D97706]/15 border border-[#D97706]/25 shrink-0">
                  <span className="text-[#D97706] text-xs font-bold">이</span>
                </div>
                <div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-[#F2F2F2] text-xs font-semibold">이지현 멘토</span>
                    <span className="text-[9px] bg-[#D97706]/10 text-[#D97706] border border-[#D97706]/20 px-1.5 py-0.5 rounded-full font-medium">AI</span>
                  </div>
                  <p className="text-[#666666] text-[10px]">{hubTitle} 허브 분석 · 조언</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={handleRefresh}
                  title="다시 분석"
                  className="w-7 h-7 flex items-center justify-center text-[#333333] hover:text-[#888888] cursor-pointer transition-colors rounded-lg hover:bg-white/[0.04]"
                >
                  <i className="ri-refresh-line text-sm"></i>
                </button>
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="w-7 h-7 flex items-center justify-center text-[#333333] hover:text-[#888888] cursor-pointer transition-colors rounded-lg hover:bg-white/[0.04]"
                >
                  <i className="ri-close-line text-base"></i>
                </button>
              </div>
            </div>

            {/* Stats Strip */}
            <div className="px-4 py-2.5 border-b border-white/[0.05] shrink-0">
              <div className="flex items-center gap-3 text-[10px]">
                <div className="flex items-center gap-1">
                  <span className="text-[#777777]">진행률</span>
                  <span className="text-[#F2F2F2] font-semibold tabular-nums">{pct}%</span>
                </div>
                <span className="text-[#444444]">·</span>
                {overdue > 0 && (
                  <div className="flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-400 shrink-0"></span>
                    <span className="text-red-400/80">초과 {overdue}</span>
                  </div>
                )}
                {urgent > 0 && (
                  <div className="flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0 animate-pulse"></span>
                    <span className="text-amber-400/80">임박 {urgent}</span>
                  </div>
                )}
                {overdue === 0 && urgent === 0 && (
                  <div className="flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0"></span>
                    <span className="text-emerald-400/70">정상 진행</span>
                  </div>
                )}
                <div className="ml-auto flex-1 max-w-[80px]">
                  <div className="w-full bg-white/[0.04] rounded-full h-1">
                    <div className="h-1 rounded-full bg-[#D97706]/60 transition-all" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
              {messages.length === 0 && !loading && (
                <div className="flex flex-col items-center justify-center py-10 text-center">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#D97706]/10 mb-3">
                    <i className="ri-robot-2-line text-[#D97706]/60 text-xl"></i>
                  </div>
                  <p className="text-[#666666] text-xs">허브 상황 분석 중...</p>
                </div>
              )}

              {messages.map((msg) =>
                msg.role === "user" ? (
                  <div key={msg.id} className="flex justify-end">
                    <div className="max-w-[85%] bg-[#4B6CF7]/10 border border-[#4B6CF7]/15 rounded-2xl rounded-tr-sm px-3.5 py-2.5 text-xs text-[#DDDDDD] leading-relaxed">
                      {msg.text}
                    </div>
                  </div>
                ) : (
                  <div key={msg.id} className="flex items-start gap-2 animate-[fadeIn_0.3s_ease]">
                    <div className="w-6 h-6 flex items-center justify-center rounded-full bg-[#D97706]/20 text-[#D97706] text-[9px] font-bold shrink-0 mt-0.5">이</div>
                    <div className="max-w-[85%] bg-[#D97706]/[0.15] border border-[#D97706]/25 rounded-2xl rounded-tl-sm px-3.5 py-2.5 text-xs text-[#E0E0E0] leading-relaxed">
                      {msg.text}
                    </div>
                  </div>
                )
              )}

              {loading && (
                <div className="flex items-start gap-2">
                  <div className="w-6 h-6 flex items-center justify-center rounded-full bg-[#D97706]/20 text-[#D97706] text-[9px] font-bold shrink-0">이</div>
                  <div className="bg-[#D97706]/[0.12] border border-[#D97706]/20 rounded-2xl rounded-tl-sm px-3.5 py-2.5">
                    <div className="flex items-center gap-1">
                      {[0, 1, 2].map((i) => (
                        <span key={i} className="w-1.5 h-1.5 rounded-full bg-[#D97706]/60 animate-bounce inline-block"
                          style={{ animationDelay: `${i * 0.15}s` }} />
                      ))}
                    </div>
                  </div>
                </div>
              )}
              <div ref={bottomRef} />
            </div>

            {/* Quick question chips */}
            {messages.length > 0 && !loading && (
              <div className="px-4 pb-2 flex flex-wrap gap-1.5 shrink-0">
                {[
                  "가장 급한 태스크는?",
                  "예산 리스크 분석해줘",
                  "이번 주 집중해야 할 것은?",
                ].map((q) => (
                  <button
                    key={q}
                    type="button"
                    onClick={() => { setInput(q); setTimeout(() => inputRef.current?.focus(), 50); }}
                    className="text-[10px] px-2.5 py-1 rounded-full border border-white/[0.08] text-[#777777] hover:border-[#D97706]/30 hover:text-[#D97706] transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {q}
                  </button>
                ))}
              </div>
            )}

            {/* Input */}
            <div className="px-4 pb-4 pt-1 border-t border-white/[0.06] shrink-0">
              <div className="flex gap-2 items-end">
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKey}
                  placeholder="멘토에게 추가 질문..."
                  rows={2}
                  maxLength={300}
                  className="flex-1 bg-white/[0.04] rounded-xl px-3 py-2 text-xs outline-none border border-white/[0.08] focus:border-[#D97706]/30 resize-none placeholder-[#555555]"
                  style={{ color: "#CCCCCC" }}
                />
                <button
                  type="button"
                  onClick={handleSend}
                  disabled={!input.trim() || loading}
                  className="w-9 h-9 flex items-center justify-center rounded-xl bg-[#D97706] text-white hover:bg-[#B45309] disabled:opacity-40 transition-all cursor-pointer whitespace-nowrap shrink-0"
                >
                  <i className="ri-send-plane-fill text-sm"></i>
                </button>
              </div>
              <p className="text-[#666666] text-[9px] mt-1.5">Enter 전송 · Shift+Enter 줄바꿈</p>
            </div>
          </>
        )}
      </div>

      {/* Backdrop on mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 z-[79] lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}
    </>
  );
}
